import GeoLocationComponent from 'components/GeoLocationComponent/GeoLocationComponent';
import type { GeoLocation } from 'components/GeoLocationComponent/GeoLocationComponent';
import { RESOURCE_SET_STATUSES } from 'components/IAP/ICSConstants';
import {
  useGetRosterForIncident,
  useGetRostersForGroup,
} from 'hooks/incident/useRoster';
import {
  useResourceRelationships,
  useUpdateResourceRelationships,
} from 'hooks/resources/useResourceRelationships';
import { useAors } from 'hooks/useAors';
import React, { useState } from 'react';
import { Form, Offcanvas, Row } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from 'slices/commonSelectors';
import Point from '@arcgis/core/geometry/Point';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { applyHeleneResourcePopupToGraphic } from '../MapPage/CorporateViewTabs/LayersTab/cfa-demo/heleneResourcePopup';
import {
  CFA_HELENE_STORES,
  CFA_HELENE_STORES_LAYER_ID,
} from '../MapPage/CorporateViewTabs/LayersTab/cfa-demo/historicalHurricanesHeleneData';
import { FACILITY_DEMO_PACKS } from '../MapPage/CorporateViewTabs/NotificationsTab/facilityDemoPack';
import FormSymbologySelect from '../MapPage/CorporateViewTabs/LayersTab/FormSymbologySelect';
import { useOptionalUscgMapContext } from '../MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import {
  heleneStoreIdFromResourceNotes,
  isHeleneConeResourceSet,
} from '../MapPage/CorporateViewTabs/NotificationsTab/memphisThreatNotification';
import DrawerWrapper, { DrawerFooter } from './DrawerWrapper';
import { EditEntityDrawerProps } from './EntityListProps';
import ResourceSelectFields from './ResourceSelectFields';
import FormDateTimeField from './formFields/FormDateTimeField';
import FormSelectField from './formFields/FormSelectField';
import FormTextAreaField from './formFields/FormTextAreaField';
import FormTextField from './formFields/FormTextField';
import RelationshipPickerField from './formFields/RelationshipPickerField';
import { useAllIncidents, useScopedIncident } from './hooks/incidentHooks';
import { useCustomResourcePersistence } from './hooks/customResourceTypeHooks';
import {
  EditResourceSetFormData,
  ResourceSet,
  useUpdateResourceSet,
} from './hooks/resourceSetHooks';
import { useResourceSites } from './hooks/resourceSiteHooks';

function geometryCoords(geometry: ResourceSet['geometry']): {
  lat?: number;
  lon?: number;
} {
  if (
    !geometry ||
    typeof geometry !== 'object' ||
    !Array.isArray(geometry.coordinates)
  ) {
    return {};
  }
  const [lon, lat] = geometry.coordinates;
  return {
    lon: lon != null ? Number(lon) : undefined,
    lat: lat != null ? Number(lat) : undefined,
  };
}

function latLngFromGeoLocation(location: GeoLocation | undefined | null): {
  lat?: number;
  lon?: number;
} {
  const coords = location?.centroid?.geometry;
  if (
    !coords ||
    coords.type !== 'Point' ||
    !Array.isArray(coords.coordinates)
  ) {
    return {};
  }
  const [lon, lat] = coords.coordinates;
  return {
    lon: typeof lon === 'number' ? lon : Number(lon),
    lat: typeof lat === 'number' ? lat : Number(lat),
  };
}

const ResourceSetEditDrawer: React.FC<EditEntityDrawerProps<ResourceSet>> = ({
  toggle,
  selectedEntity: editedEntity,
}) => {
  const initialCoords = geometryCoords(editedEntity.geometry);
  const form = useForm<EditResourceSetFormData>({
    mode: 'onChange',
    defaultValues: {
      id: editedEntity.id,
      resourceSetId: editedEntity.resource_set_id,
      resourceSiteId: editedEntity.resource_site_id,
      groupGuid: editedEntity.group_guid,
      name: editedEntity.name,
      description: editedEntity.description,
      geolocation: editedEntity.geolocation,
      kind: editedEntity.kind,
      type: editedEntity.type,
      quantity: editedEntity.quantity,
      status: editedEntity.status,
      consolidationTargetId: editedEntity.consolidation_target_id,
      archived: editedEntity.archived,
      dateTimeOrdered: editedEntity.date_time_ordered,
      eta: editedEntity.eta,
      incidents: editedEntity.incidents,
      members: [],
      sourceLocation: editedEntity.source_location,
      sourceResourceSiteId: editedEntity.source_resource_site_id,
      destinationLocation: editedEntity.destination_location,
      destinationResourceSiteId: editedEntity.destination_resource_site_id,
      notes: editedEntity.notes || '',
      threats: editedEntity.threats ?? [],
      threatChecklists: editedEntity.threat_checklists ?? {},
      automationRules: editedEntity.automation_rules ?? [],
      automationRuleChecklists: editedEntity.automation_rule_checklists ?? {},
      owningOrganization: editedEntity.owning_organization || '',
      unit: editedEntity.unit || '',
      address: editedEntity.address || '',
      hullTailNumber: editedEntity.hull_tail_number || '',
      pointOfContact: editedEntity.point_of_contact || '',
      opcon: editedEntity.opcon || '',
      tacon: editedEntity.tacon || '',
      symbology: editedEntity.symbology || '',
      aor: editedEntity.aor || '',
      latitude: initialCoords.lat != null ? String(initialCoords.lat) : '',
      longitude: initialCoords.lon != null ? String(initialCoords.lon) : '',
      geometry: editedEntity.geometry ?? null,
    },
  });
  const { setValue, watch } = form;
  const watchedStatus = watch('status');

  const { data: resourceSites } = useResourceSites();
  const { data: aors = [] } = useAors();
  const groupGuid = useSelector(selectGroupGuid);
  const { data: groupMembers = [] } = useGetRostersForGroup(groupGuid || '');
  const mapContext = useOptionalUscgMapContext();
  const mapView = mapContext?.mapView ?? null;

  const mutation = useUpdateResourceSet(editedEntity.resource_set_id);
  const [geolocation, setGeolocation] = useState(editedEntity.geolocation);
  const [sourceLocation, setSourceLocation] = useState(
    editedEntity.source_location
  );
  const [destinationLocation, setDestinationLocation] = useState(
    editedEntity.destination_location
  );
  const { persistPendingResources } = useCustomResourcePersistence();

  // Location type toggles - initialize based on existing data
  const [useCustomCurrentLocation, setUseCustomCurrentLocation] = useState(
    !!editedEntity.geolocation
  );
  const [useCustomSourceLocation, setUseCustomSourceLocation] = useState(
    !!editedEntity.source_location
  );
  const [useCustomDestinationLocation, setUseCustomDestinationLocation] =
    useState(!!editedEntity.destination_location);

  const syncLatLongFromGeoLocation = (location: GeoLocation | undefined) => {
    const { lat, lon } = latLngFromGeoLocation(location);
    if (lat != null && !Number.isNaN(lat)) {
      setValue('latitude', String(lat), { shouldDirty: true });
    }
    if (lon != null && !Number.isNaN(lon)) {
      setValue('longitude', String(lon), { shouldDirty: true });
    }
  };

  const handleGeolocationChange = (location: GeoLocation) => {
    setGeolocation(location);
    syncLatLongFromGeoLocation(location);
  };

  // Fetch incidents and current incident with proper types
  const { data: incidents = [] } = useAllIncidents();
  const currentIncident = useScopedIncident();

  // Fetch incident members and relationships
  const { data: rosterData = [] } = useGetRosterForIncident(
    currentIncident?.id
  );
  const { data: relationships = [] } = useResourceRelationships(
    editedEntity.resource_set_id,
    'resource'
  );
  const updateRelationships = useUpdateResourceRelationships();

  // Set the default incidents value
  React.useEffect(() => {
    if (editedEntity.incidents && editedEntity.incidents.length > 0) {
      setValue('incidents', editedEntity.incidents);
    } else if (currentIncident?.id) {
      setValue('incidents', [currentIncident.id]);
    }
  }, [currentIncident, editedEntity.incidents, setValue]);

  // Set default members value from relationships
  React.useEffect(() => {
    if (relationships.length > 0) {
      setValue(
        'members',
        relationships.map((r) => r.id)
      );
    }
  }, [relationships, setValue]);

  const syncHeleneStoreGraphic = (
    formData: EditResourceSetFormData,
    geometry: { type: string; coordinates: [number, number] } | null
  ) => {
    const facilityPack = Object.values(FACILITY_DEMO_PACKS).find((pack) =>
      pack.isResourceSet(editedEntity)
    );
    if (
      (!isHeleneConeResourceSet(editedEntity) && !facilityPack) ||
      !mapView?.map ||
      !geometry
    ) {
      return;
    }
    const [lon, lat] = geometry.coordinates;
    if (Number.isNaN(lon) || Number.isNaN(lat)) return;

    const storeId = facilityPack
      ? facilityPack.facilityIdFromResourceNotes(editedEntity.notes)
      : heleneStoreIdFromResourceNotes(editedEntity.notes);
    const store = storeId
      ? facilityPack
        ? (() => {
            const facility = facilityPack.facilities.find(
              (entry) => entry.id === storeId
            );
            return facility
              ? {
                  id: facility.id,
                  storeNumber: '',
                  name: facility.name,
                  city: facility.city,
                  state: facility.state,
                  lon: facility.lon,
                  lat: facility.lat,
                  inCone: true,
                }
              : undefined;
          })()
        : CFA_HELENE_STORES.find((entry) => entry.id === storeId)
      : undefined;
    const layer = mapView.map.findLayerById(
      facilityPack ? facilityPack.assetsLayerId : CFA_HELENE_STORES_LAYER_ID
    ) as GraphicsLayer | undefined;
    const graphic = layer?.graphics.toArray().find((g) => {
      const attrs = g.attributes ?? {};
      if (storeId && String(attrs.id ?? '') === storeId) return true;
      if (
        store?.storeNumber &&
        String(attrs.storeNumber ?? '') === store.storeNumber
      ) {
        return true;
      }
      return false;
    });
    if (!graphic) return;

    graphic.geometry = new Point({ longitude: lon, latitude: lat });
    const updatedResource: ResourceSet = {
      ...editedEntity,
      name: formData.name,
      description: formData.description,
      address: formData.address,
      owning_organization: formData.owningOrganization,
      unit: formData.unit,
      hull_tail_number: formData.hullTailNumber,
      point_of_contact: formData.pointOfContact,
      opcon: formData.opcon,
      tacon: formData.tacon,
      symbology: formData.symbology,
      quantity: formData.quantity,
      geometry,
    };
    applyHeleneResourcePopupToGraphic({
      graphic,
      resource: updatedResource,
      store,
      aorNames: {
        opcon: aors.find((a) => a.id === formData.opcon)?.name,
        tacon: aors.find((a) => a.id === formData.tacon)?.name,
      },
    });
  };

  const onSubmit = async (formData: EditResourceSetFormData) => {
    await persistPendingResources(form.watch('kind'), form.watch('type'));
    await submitResourceSetEdit(formData);
  };

  const submitResourceSetEdit = async (formData: EditResourceSetFormData) => {
    const { latitude, longitude, ...rest } = formData;
    const lat = Number(latitude);
    const lng = Number(longitude);
    const geometry =
      latitude && longitude && !Number.isNaN(lat) && !Number.isNaN(lng)
        ? {
            type: 'Point' as const,
            coordinates: [lng, lat] as [number, number],
          }
        : (formData.geometry ?? null);

    const payload = {
      ...rest,
      resourceSetId: editedEntity.resource_set_id,
      // Current location - either custom or site, not both
      geolocation: useCustomCurrentLocation ? geolocation : null,
      resourceSiteId: useCustomCurrentLocation ? null : formData.resourceSiteId,
      // Source location - either custom or site, not both
      sourceLocation: useCustomSourceLocation ? sourceLocation : null,
      sourceResourceSiteId: useCustomSourceLocation
        ? null
        : formData.sourceResourceSiteId,
      // Destination location - either custom or site, not both
      destinationLocation: useCustomDestinationLocation
        ? destinationLocation
        : null,
      destinationResourceSiteId: useCustomDestinationLocation
        ? null
        : formData.destinationResourceSiteId,
      incidents: editedEntity.incidents, // Keep existing incidents - don't allow modification
      geometry,
      pointOfContact: formData.pointOfContact || undefined,
      opcon: formData.opcon || undefined,
      tacon: formData.tacon || undefined,
      owningOrganization: formData.owningOrganization || undefined,
      unit: formData.unit || undefined,
      address: formData.address || undefined,
      hullTailNumber: formData.hullTailNumber || undefined,
      symbology: formData.symbology || undefined,
    };

    // First save the resource
    await mutation.mutateAsync(payload);

    // Then update the member relationships if they've changed
    if (formData.members) {
      await updateRelationships.mutateAsync({
        entityId: editedEntity.resource_set_id,
        entityType: 'resource',
        relatedIds: formData.members,
      });
    }

    syncHeleneStoreGraphic(formData, geometry);

    toggle();
  };

  const toggleActive = async () => {
    const newArchivedValue = !editedEntity.archived;
    // Convert snake_case entity to camelCase for the mutation
    const payload: EditResourceSetFormData = {
      id: editedEntity.id,
      resourceSetId: editedEntity.resource_set_id,
      resourceSiteId: editedEntity.resource_site_id,
      groupGuid: editedEntity.group_guid,
      name: editedEntity.name,
      description: editedEntity.description,
      geolocation: editedEntity.geolocation,
      kind: editedEntity.kind,
      type: editedEntity.type,
      quantity: editedEntity.quantity,
      status: editedEntity.status,
      consolidationTargetId: editedEntity.consolidation_target_id,
      archived: newArchivedValue,
      dateTimeOrdered: editedEntity.date_time_ordered,
      eta: editedEntity.eta,
      incidents: editedEntity.incidents,
      members: [],
      sourceLocation: editedEntity.source_location,
      sourceResourceSiteId: editedEntity.source_resource_site_id,
      destinationLocation: editedEntity.destination_location,
      destinationResourceSiteId: editedEntity.destination_resource_site_id,
      notes: editedEntity.notes,
      threats: editedEntity.threats ?? [],
      threatChecklists: editedEntity.threat_checklists ?? {},
      automationRules: editedEntity.automation_rules ?? [],
      automationRuleChecklists: editedEntity.automation_rule_checklists ?? {},
      owningOrganization: editedEntity.owning_organization,
      unit: editedEntity.unit,
      address: editedEntity.address,
      hullTailNumber: editedEntity.hull_tail_number,
      pointOfContact: editedEntity.point_of_contact || undefined,
      opcon: editedEntity.opcon || undefined,
      tacon: editedEntity.tacon || undefined,
      symbology: editedEntity.symbology,
      aor: editedEntity.aor || undefined,
      geometry: editedEntity.geometry ?? null,
    };
    await mutation.mutateAsync(payload);
    toggle();
  };

  return (
    <DrawerWrapper toggle={toggle} title="Edit Resource">
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <Offcanvas.Body>
            <Row>
              <FormTextField
                title="Name"
                fieldName="name"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 300,
                    message: 'This maximum length is 300 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormTextAreaField
                title="Capabilities"
                fieldName="description"
                variant="full"
              />
            </Row>
            <ResourceSelectFields labels />
            <Row>
              <FormTextField
                title="Quantity"
                fieldName="quantity"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 300,
                    message: 'This maximum length is 300 characters',
                  },
                }}
                variant="half"
              />
              <FormTextField title="Unit" fieldName="unit" variant="half" />
            </Row>
            <Row>
              <FormTextField
                title="Hull/Tail Number"
                fieldName="hullTailNumber"
                variant="half"
              />
              <FormTextField
                title="Owning Organization"
                fieldName="owningOrganization"
                variant="half"
              />
            </Row>
            <Row>
              <FormSelectField
                title="OPCON"
                fieldName="opcon"
                options={aors.map((aor) => ({
                  value: aor.id,
                  label: aor.name,
                }))}
                isClearable
                variant="half"
              />
              <FormSelectField
                title="TACON"
                fieldName="tacon"
                options={aors.map((aor) => ({
                  value: aor.id,
                  label: aor.name,
                }))}
                isClearable
                variant="half"
              />
            </Row>
            <Row>
              <FormSelectField
                title="Point of Contact"
                fieldName="pointOfContact"
                options={groupMembers.map((member) => ({
                  label: member.email_address || member.user_name,
                  value: member.user_guid,
                }))}
                isClearable
                variant="half"
              />
              <FormTextField
                title="Address"
                fieldName="address"
                variant="half"
              />
            </Row>
            <Row className="align-items-start">
              <div className="col-md-6">
                <FormSymbologySelect
                  excludeOptions={['completedBoom', 'proposedBoom', 'oilSpill']}
                />
              </div>
              <FormTextField
                title="Latitude"
                fieldName="latitude"
                variant="half"
              />
            </Row>
            <Row>
              <FormTextField
                title="Longitude"
                fieldName="longitude"
                variant="half"
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="Date Time Ordered"
                fieldName="dateTimeOrdered"
                variant="half"
                type="datetime-local"
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="ETA"
                fieldName="eta"
                variant="half"
                type="datetime-local"
              />
            </Row>
            <Row>
              <FormSelectField
                title="Set Status"
                fieldName="status"
                rules={{
                  required: { value: true, message: 'This field is required' },
                }}
                options={
                  RESOURCE_SET_STATUSES.map((u) => ({
                    label: u.label,
                    value: u.value,
                  })) || []
                }
                variant="half"
              />
            </Row>
            {/* Location fields - conditional based on status */}
            {(watchedStatus === 'In Transit' ||
              watchedStatus === 'Ordered') && (
              <>
                <Row>
                  <div className="mb-3">
                    <h6>Location Information</h6>
                  </div>
                </Row>

                {/* Source Location */}
                <Row className="mb-3">
                  <div className="col-12">
                    <Form.Check
                      type="switch"
                      id="use-custom-source-location"
                      label="Use Custom Source Location"
                      checked={useCustomSourceLocation}
                      onChange={(e) => {
                        setUseCustomSourceLocation(e.target.checked);
                        if (e.target.checked) {
                          setValue('sourceResourceSiteId', '');
                        } else {
                          setSourceLocation(undefined);
                        }
                      }}
                      className="mb-2"
                    />
                    {useCustomSourceLocation ? (
                      <div>
                        <label className="form-label">Source Location:</label>
                        <GeoLocationComponent
                          location={sourceLocation}
                          setLocation={setSourceLocation}
                        />
                      </div>
                    ) : (
                      <FormSelectField
                        title="Source Site"
                        fieldName="sourceResourceSiteId"
                        options={
                          resourceSites
                            ?.filter((r) => !r.archived)
                            ?.map((r) => ({ label: r.name, value: r.id })) || []
                        }
                        variant="full"
                      />
                    )}
                  </div>
                </Row>

                {/* Current Location */}
                <Row className="mb-3">
                  <div className="col-12">
                    <Form.Check
                      type="switch"
                      id="use-custom-current-location"
                      label="Use Custom Current Location"
                      checked={useCustomCurrentLocation}
                      onChange={(e) => {
                        setUseCustomCurrentLocation(e.target.checked);
                        if (e.target.checked) {
                          setValue('resourceSiteId', '');
                        } else {
                          setGeolocation(undefined);
                        }
                      }}
                      className="mb-2"
                    />
                    {useCustomCurrentLocation ? (
                      <div>
                        <label className="form-label">Current Location:</label>
                        <GeoLocationComponent
                          location={geolocation}
                          setLocation={handleGeolocationChange}
                        />
                      </div>
                    ) : (
                      <FormSelectField
                        title="Current Site"
                        fieldName="resourceSiteId"
                        options={
                          resourceSites
                            ?.filter((r) => !r.archived)
                            ?.map((r) => ({ label: r.name, value: r.id })) || []
                        }
                        variant="full"
                      />
                    )}
                  </div>
                </Row>

                {/* Destination Location */}
                <Row className="mb-3">
                  <div className="col-12">
                    <Form.Check
                      type="switch"
                      id="use-custom-destination-location"
                      label="Use Custom Destination Location"
                      checked={useCustomDestinationLocation}
                      onChange={(e) => {
                        setUseCustomDestinationLocation(e.target.checked);
                        if (e.target.checked) {
                          setValue('destinationResourceSiteId', '');
                        } else {
                          setDestinationLocation(undefined);
                        }
                      }}
                      className="mb-2"
                    />
                    {useCustomDestinationLocation ? (
                      <div>
                        <label className="form-label">
                          Destination Location:
                        </label>
                        <GeoLocationComponent
                          location={destinationLocation}
                          setLocation={setDestinationLocation}
                        />
                      </div>
                    ) : (
                      <FormSelectField
                        title="Destination Site"
                        fieldName="destinationResourceSiteId"
                        options={
                          resourceSites
                            ?.filter((r) => !r.archived)
                            ?.map((r) => ({ label: r.name, value: r.id })) || []
                        }
                        variant="full"
                      />
                    )}
                  </div>
                </Row>
              </>
            )}

            {/* Non-transit resources - only current location */}
            {watchedStatus &&
              watchedStatus !== 'In Transit' &&
              watchedStatus !== 'Ordered' && (
                <Row className="mb-3">
                  <div className="col-12">
                    <Form.Check
                      type="switch"
                      id="use-custom-current-location"
                      label="Use Custom Location"
                      checked={useCustomCurrentLocation}
                      onChange={(e) => {
                        setUseCustomCurrentLocation(e.target.checked);
                        if (e.target.checked) {
                          setValue('resourceSiteId', '');
                        } else {
                          setGeolocation(undefined);
                        }
                      }}
                      className="mb-2"
                    />
                    {useCustomCurrentLocation ? (
                      <div>
                        <label className="form-label">Current Location:</label>
                        <GeoLocationComponent
                          location={geolocation}
                          setLocation={handleGeolocationChange}
                        />
                      </div>
                    ) : (
                      <FormSelectField
                        title="Resource Site"
                        fieldName="resourceSiteId"
                        options={
                          resourceSites
                            ?.filter((r) => !r.archived)
                            ?.map((r) => ({ label: r.name, value: r.id })) || []
                        }
                        variant="full"
                      />
                    )}
                  </div>
                </Row>
              )}
            <Row>
              <div className="mb-3">
                <label className="form-label">Incidents</label>
                {editedEntity.incidents && editedEntity.incidents.length > 0 ? (
                  <ul className="list-group">
                    {editedEntity.incidents.map((incidentId) => {
                      const incident = incidents.find(
                        (i) => i.id === incidentId
                      );
                      return (
                        <li key={incidentId} className="list-group-item">
                          {incident?.name || incidentId}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="text-muted">
                    <small>
                      No incidents assigned. Resources will be automatically
                      added when assigned to incidents.
                    </small>
                  </div>
                )}
              </div>
            </Row>
            <Row>
              <div className="text-muted">
                <small>
                  The available members are those that are found in the roster
                  of the currently selected incident. The currently selected
                  incident is {currentIncident?.name}
                </small>
              </div>
              <RelationshipPickerField
                label="Assigned Members"
                fieldName="members"
                defaultValue={relationships.map((r) => r.id)}
                options={
                  rosterData?.map((member) => ({
                    ...member,
                    id: member.incidentMemberId,
                  })) || []
                }
                identity={(member) => member.id}
                title={(member) => member.emailAddress || member.userName || ''}
                cardType="incident-member"
              />
            </Row>
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--tertiary button--reverse"
                onClick={toggleActive}
              >
                {(!editedEntity.archived && 'Deactivate') || 'Activate'}
              </StylishNewButton>
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={mutation.isPending}
              >
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default ResourceSetEditDrawer;
